import api from '../../../lib/api';
import type {
  CreateFacebookChannelRequest,
  FacebookChannel,
  FacebookUserWhitelistEntry,
  UpdateMetaDmChannelRequest,
} from '@workspace/types';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export const facebookAdminApi = {
  async getChannelByInstanceBot(instanceBotId: string): Promise<FacebookChannel | null> {
    const channels = await this.listChannels();
    return channels.find(channel => channel.instanceBotId === instanceBotId) ?? null;
  },

  async listChannels(): Promise<FacebookChannel[]> {
    const response = await api.get<ApiResponse<FacebookChannel[]>>('/admin/facebook/channels');
    return requireData(response, 'No se pudieron cargar los canales Facebook');
  },

  async createChannel(payload: CreateFacebookChannelRequest): Promise<FacebookChannel> {
    const response = await api.post<ApiResponse<FacebookChannel>>('/admin/facebook/channels', payload);
    return requireData(response, 'No se pudo crear el canal Facebook');
  },

  async updateChannel(id: string, payload: UpdateMetaDmChannelRequest): Promise<FacebookChannel> {
    const response = await api.patch<ApiResponse<FacebookChannel>>(
      `/admin/facebook/channels/${id}`,
      payload,
    );
    return requireData(response, 'No se pudo actualizar el canal Facebook');
  },

  async deleteChannel(id: string): Promise<void> {
    await api.delete(`/admin/facebook/channels/${id}`);
  },

  async listWhitelist(channelId: string): Promise<FacebookUserWhitelistEntry[]> {
    const response = await api.get<ApiResponse<FacebookUserWhitelistEntry[]>>(
      `/admin/facebook/channels/${channelId}/whitelist`,
    );
    return requireData(response, 'No se pudo cargar la whitelist');
  },

  async addUser(channelId: string, psid: string, label?: string): Promise<FacebookUserWhitelistEntry> {
    const response = await api.post<ApiResponse<FacebookUserWhitelistEntry>>(
      `/admin/facebook/channels/${channelId}/whitelist`,
      { psid, label },
    );
    return requireData(response, 'No se pudo agregar el usuario');
  },

  async removeUser(channelId: string, userId: string): Promise<void> {
    await api.delete(
      `/admin/facebook/channels/${channelId}/whitelist/${encodeURIComponent(userId)}`,
    );
  },
};
